import React, { useEffect, useState, useCallback } from 'react';
import { api, fmtMoney, formatApiError } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';
import { ExternalLink, RefreshCw, Receipt, Zap, RotateCcw } from 'lucide-react';

const STATUS = (s) => {
  if (s === 'paid') return <span className="tag-pill ok">Paid</span>;
  if (s === 'pending') return <span className="tag-pill warn">Pending</span>;
  if (s === 'refunded') return <span className="tag-pill muted">Refunded</span>;
  if (s === 'expired' || s === 'failed') return <span className="tag-pill err">{s}</span>;
  return <span className="tag-pill muted">{s}</span>;
};

const Orders = ({ lob }) => {
  const darkMode = useDarkMode();
  const aBg = darkMode ? lob?.accentBgDark : lob?.accentBg;
  const aTc = darkMode ? lob?.accentTcDark : lob?.accentTc;
  const [orders, setOrders] = useState([]);
  const [stripe, setStripe] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    if (!lob) return;
    const url = `/orders?lob_id=${lob.id}${statusFilter ? `&status=${statusFilter}` : ''}`;
    api.get(url).then((r) => setOrders(r.data)).catch((e) => setErr(formatApiError(e)));
    api.get('/settings/integrations/stripe').then((r) => setStripe(r.data)).catch(() => {});
  }, [lob?.id, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const refund = async (o) => {
    if (!window.confirm(`Refund ${fmtMoney(o.amount)} for "${o.product_name}"?`)) return;
    try { await api.post(`/orders/${o.id}/refund`); load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  const stats = orders.reduce((acc, o) => {
    if (o.status === 'paid') { acc.paid += 1; acc.revenue += o.amount; }
    if (o.upsell_for_order_id) acc.upsells += 1;
    return acc;
  }, { paid: 0, revenue: 0, upsells: 0 });

  return (
    <>
      <div className="page-toolbar" data-testid="orders-toolbar">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="page-title">Orders</span>
          <span className="lob-badge" style={{ background: aBg }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lob.accent, display: 'inline-block', marginRight: 4 }} />
            <span style={{ color: aTc }}>{lob.label}</span>
          </span>
        </div>
        <select className="sel-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="order-status-filter">
          <option value="">All statuses</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="refunded">Refunded</option>
          <option value="expired">Expired</option>
        </select>
        <button className="dp-btn" onClick={load} data-testid="refresh-orders"><RefreshCw size={11} style={{ marginRight: 4 }} />Refresh</button>
      </div>
      {err && <div className="module-banner err">{err}</div>}

      <div className="content-area">
        <div className="kpi-grid" style={{ marginBottom: 10 }}>
          <div className="kpi-card"><div className="kpi-label">Paid orders</div><div className="kpi-value">{stats.paid}</div></div>
          <div className="kpi-card"><div className="kpi-label">Revenue</div><div className="kpi-value">{fmtMoney(stats.revenue)}</div></div>
          <div className="kpi-card"><div className="kpi-label">Upsells taken</div><div className="kpi-value">{stats.upsells}</div></div>
          <div className="kpi-card"><div className="kpi-label">Total orders</div><div className="kpi-value">{orders.length}</div></div>
        </div>

        {orders.length === 0 ? (
          <div className="empty-state" data-testid="orders-empty">
            <Receipt size={36} style={{ opacity: 0.25 }} />
            <div className="ttl">No orders yet</div>
            <div className="sub">Share a product buy-link to start selling.</div>
          </div>
        ) : (
          <div className="card">
            <table className="tbl">
              <thead><tr><th>When</th><th>Product</th><th>Customer</th><th>Type</th><th>Amount</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} data-testid={`order-row-${o.id}`}>
                    <td>{new Date(o.created_at).toLocaleString()}</td>
                    <td>
                      {o.product_name}
                      {o.upsell_for_order_id && <span className="tag-pill ok" style={{ marginLeft: 6 }}><Zap size={9} /> upsell</span>}
                    </td>
                    <td>{o.customer_email || '—'}</td>
                    <td>{o.type === 'subscription' ? `${o.interval} sub` : 'one-time'}</td>
                    <td><b>{fmtMoney(o.amount)}</b></td>
                    <td>{STATUS(o.status)}</td>
                    <td className="tbl-actions">
                      {o.receipt_url && <a className="btn-link" href={o.receipt_url} target="_blank" rel="noreferrer" data-testid={`receipt-${o.id}`}><ExternalLink size={11} /></a>}
                      {o.status === 'paid' && !o.upsell_for_order_id && stripe?.supports_refunds && (
                        <button className="btn-link" style={{ color: 'var(--error)' }} onClick={() => refund(o)} data-testid={`refund-${o.id}`}><RotateCcw size={11} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default Orders;
